import { useState } from 'react';

import type { EndsInspectionHistory, EndsInspectionRecord } from '@ballerine/common';

import { ctw } from '@/common';
import { useForceExpandAccordions } from '@/common/providers/PrintContext';

type FdaInspectionHistoryProps = {
  inspectionHistory: EndsInspectionHistory;
};

const VISIBLE_RECORDS = 5;

const isPassing = (status: string | null | undefined): boolean => {
  const normalized = (status ?? '').toLowerCase();

  return normalized.includes('pass') || normalized.includes('no violation');
};

const StatBox = ({
  value,
  label,
  tone,
}: {
  value: number | null | undefined;
  label: string;
  tone: 'neutral' | 'good' | 'bad';
}) => (
  <div className="flex flex-col gap-1 rounded-xl border border-gray-200 px-4 py-3">
    <span className="text-xs uppercase tracking-wide text-slate-500">{label}</span>
    <span
      className={ctw(
        'text-2xl font-semibold',
        tone === 'good' && 'text-green-600',
        tone === 'bad' && 'text-red-600',
        tone === 'neutral' && 'text-slate-900',
      )}
    >
      {value ?? 0}
    </span>
  </div>
);

const InspectionRow = ({ record }: { record: EndsInspectionRecord }) => {
  const passed = isPassing(record.status);

  return (
    <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-4 py-3 last:border-b-0">
      <div className="flex min-w-0 flex-1 items-start gap-4">
        <span className="w-24 shrink-0 text-sm text-slate-500">{record.date ?? '—'}</span>
        <div className="min-w-0">
          <span className="text-sm font-semibold text-slate-900">{record.type ?? '—'}</span>
          {record.subtype && (
            <span className="text-sm text-slate-500"> &mdash; {record.subtype}</span>
          )}
          {record.flags && record.flags.length > 0 && (
            <span className="ml-2 text-sm text-amber-600">
              {record.flags.map(flag => `⚠ ${flag}`).join('  ')}
            </span>
          )}
        </div>
      </div>
      {record.status && (
        <span
          className={ctw(
            'shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium uppercase',
            passed ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700',
          )}
        >
          {passed ? 'Passed' : 'Failed'}
        </span>
      )}
    </div>
  );
};

export const FdaInspectionHistory = ({ inspectionHistory }: FdaInspectionHistoryProps) => {
  const [expanded, setExpanded] = useState(false);
  // Print/PDF renders have no interaction — show every record.
  const forceExpandAccordions = useForceExpandAccordions();
  const showAll = expanded || forceExpandAccordions;
  const { stats, records, note } = inspectionHistory;

  const allRecords = records ?? [];
  const visibleRecords = showAll ? allRecords : allRecords.slice(0, VISIBLE_RECORDS);
  const hiddenCount = allRecords.length - VISIBLE_RECORDS;

  return (
    <div className="mt-8">
      <h4 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-900">
        Inspection History
      </h4>

      {stats && (
        <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          <StatBox value={stats.inspections} label="Inspections" tone="neutral" />
          <StatBox value={stats.noViolations} label="No Violations" tone="good" />
          <StatBox
            value={stats.warningLetters}
            label="Warning Letters"
            tone={(stats.warningLetters ?? 0) > 0 ? 'bad' : 'good'}
          />
          <StatBox value={stats.cmps} label="CMPs" tone={(stats.cmps ?? 0) > 0 ? 'bad' : 'good'} />
          <StatBox
            value={stats.underageSales}
            label="Underage Sales"
            tone={(stats.underageSales ?? 0) > 0 ? 'bad' : 'good'}
          />
        </div>
      )}

      {allRecords.length > 0 && (
        <div className="rounded-2xl border border-gray-200">
          {visibleRecords.map((record, index) => (
            <InspectionRow key={index} record={record} />
          ))}
          {hiddenCount > 0 && !forceExpandAccordions && (
            <div className="px-4 py-3 text-center">
              <button
                type="button"
                onClick={() => setExpanded(prev => !prev)}
                className="text-sm text-slate-500 hover:text-slate-700"
              >
                {expanded
                  ? 'Show less'
                  : `… ${hiddenCount} older inspection${hiddenCount === 1 ? '' : 's'} — Show all`}
              </button>
            </div>
          )}
        </div>
      )}

      {note && <p className="mt-3 text-xs italic text-slate-400">{note}</p>}
    </div>
  );
};
